import { useEffect, useMemo, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Link } from "react-router-dom"
import { AmpelBadge } from "@/components/AmpelBadge"
import {
  createWartung,
  deleteWartung,
  dupliziereWartung,
  erstelleWartungServiceticket,
  fetchAnlagen,
  fetchWartung,
  fetchWartungen,
  fetchWartungsarten,
  updateWartung,
  type Wartung,
  type WartungEingabe,
} from "@/lib/api"
import { Button, Input, Select, Textarea } from "@/components/ui"
import { DataGrid, type ColumnDef } from "@/components/DataGrid"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SHEET_WIDTH,
} from "@/components/Sheet"
import { useToast, fehlerNachricht } from "@/lib/toast"

const LEER: WartungEingabe = {
  wartungsartId: 0,
  komponenteId: null,
  objektId: null,
  anlageId: null,
  wartungsdatum: null,
  status: null,
  aktiv: true,
  ergebnis: null,
  protokollPaperlessId: null,
}

/** Wartungs-/Inspektionsliste mit Ampel-Spalte (AP-3.4, 02-W) + Kontextspalte Anlage/Filter (P2).
 * Anlegen/Bearbeiten geschieht im Sheet von rechts (UX_KONZEPT.md §7) statt auf
 * einer eigenen Route — die Route `/maschinen/wartungen/:id` bleibt als
 * Deep-Link-Ziel (Suche, externe Links) erhalten und zeigt weiterhin
 * WartungDetailPage. */
export function WartungenPage() {
  const [anlageFilter, setAnlageFilter] = useState<string>("")
  const [sheetId, setSheetId] = useState<number | "neu" | null>(null)
  const queryClient = useQueryClient()

  const { data: wartungen, isLoading, isError, refetch } = useQuery({
    queryKey: ["wartungen"],
    queryFn: () => fetchWartungen(),
  })
  const { data: arten } = useQuery({ queryKey: ["wartungsarten"], queryFn: fetchWartungsarten })
  const { data: anlagen } = useQuery({ queryKey: ["anlagen"], queryFn: fetchAnlagen })

  const artName = (id: number) => arten?.find((a) => a.id === id)?.titel ?? `Art #${id}`
  const anlageName = (id: number | null) =>
    id === null ? "—" : anlagen?.find((a) => a.id === id)?.bezeichnung ?? `Anlage #${id}`

  const gefiltert = useMemo(() => {
    if (!anlageFilter) return wartungen ?? []
    return (wartungen ?? []).filter((w) => w.anlageId === Number(anlageFilter))
  }, [wartungen, anlageFilter])

  // Grid-Spalten: header als String, damit die Spalten-Auswahl ihn anzeigen kann
  const spalten = useMemo<ColumnDef<Wartung>[]>(
    () => [
      {
        id: "wartungsart",
        header: "Wartungs-/Inspektionsart",
        accessorFn: (w) => artName(w.wartungsartId),
        cell: ({ row }) => (
          <button
            type="button"
            onClick={() => setSheetId(row.original.id)}
            className="rounded-md text-left hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
          >
            {artName(row.original.wartungsartId)}
          </button>
        ),
        meta: { cellClassName: "text-slate-900" },
      },
      {
        id: "anlage",
        header: "Anlage",
        accessorFn: (w) =>
          w.anlageId !== null
            ? anlageName(w.anlageId)
            : w.komponenteId !== null
              ? `Komponente #${w.komponenteId}`
              : "",
        cell: ({ row }) => {
          const w = row.original
          return w.anlageId !== null ? (
            <Link to={`/maschinen/anlagen/${w.anlageId}`} className="hover:underline">
              {anlageName(w.anlageId)}
            </Link>
          ) : w.komponenteId !== null ? (
            <Link to={`/maschinen/komponenten/${w.komponenteId}`} className="hover:underline">
              Komponente #{w.komponenteId}
            </Link>
          ) : (
            "—"
          )
        },
      },
      {
        id: "wartungsdatum",
        header: "Wartungsdatum",
        accessorFn: (w) => w.wartungsdatum ?? "",
        cell: ({ row }) => row.original.wartungsdatum ?? "—",
      },
      {
        id: "ablaufdatum",
        header: "Ablaufdatum",
        accessorFn: (w) => w.ablaufdatum ?? "",
        cell: ({ row }) => row.original.ablaufdatum ?? "—",
      },
      {
        id: "status",
        header: "Status",
        accessorFn: (w) => w.status ?? "",
        cell: ({ row }) => row.original.status ?? "—",
      },
      {
        id: "ampel",
        header: "Ampel",
        accessorFn: (w) => w.ampel,
        cell: ({ row }) => <AmpelBadge ampel={row.original.ampel} />,
      },
    ],
    // artName/anlageName hängen an den Query-Daten
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [arten, anlagen],
  )

  return (
    <div>
      <header className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Wartung &amp; Inspektion</h1>
          <p className="mt-1 text-sm text-slate-600">
            Dieselbe Fristen-/Ampel-Engine wie Prüfungen (§5) — konsistente Schaltjahrlogik.
          </p>
        </div>
        <Button type="button" onClick={() => setSheetId("neu")}>
          + Neue Wartung
        </Button>
      </header>

      <WartungSheet
        wartungId={sheetId}
        onOpenChange={(open) => !open && setSheetId(null)}
        onGespeichert={() => queryClient.invalidateQueries({ queryKey: ["wartungen"] })}
      />

      <div className="mb-4">
        <Select
          value={anlageFilter}
          onChange={(e) => setAnlageFilter(e.target.value)}
          className="w-auto"
        >
          <option value="">Alle Anlagen</option>
          {anlagen?.map((a) => (
            <option key={a.id} value={a.id}>
              {a.bezeichnung}
            </option>
          ))}
        </Select>
      </div>

      {isLoading && (
        <div className="animate-pulse space-y-2" aria-label="Lade Wartungen …">
          <div className="h-8 rounded-md bg-slate-100" />
          <div className="h-8 rounded-md bg-slate-100" />
          <div className="h-8 rounded-md bg-slate-100" />
        </div>
      )}
      {isError && (
        <p className="text-sm text-red-700">
          Wartungen konnten nicht geladen werden.{" "}
          <button
            type="button"
            onClick={() => refetch()}
            className="rounded-md underline hover:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
          >
            Erneut versuchen
          </button>
        </p>
      )}

      {wartungen && (
        <DataGrid
          columns={spalten}
          data={gefiltert}
          storageKey="wartungen"
          emptyMessage={
            anlageFilter
              ? "Keine Wartungen/Inspektionen für diese Anlage."
              : "Keine Wartungen/Inspektionen erfasst."
          }
        />
      )}
    </div>
  )
}

/** Sheet-Formular für Anlegen/Bearbeiten einer Wartung/Inspektion (UX_KONZEPT.md §7.3). */
export function WartungSheet({
  wartungId,
  onOpenChange,
  onGespeichert,
  vorbelegung,
}: {
  wartungId: number | "neu" | null
  onOpenChange: (open: boolean) => void
  onGespeichert: () => void
  /** Vorbelegung beim Anlegen — z. B. Komponente aus der Komponentenansicht (Issue #5) */
  vorbelegung?: Partial<WartungEingabe>
}) {
  const istNeu = wartungId === "neu"
  const id = istNeu || wartungId === null ? null : wartungId
  const toast = useToast()

  const [form, setForm] = useState<WartungEingabe>(LEER)

  const { data: wartung } = useQuery({
    queryKey: ["wartungen", id],
    queryFn: () => fetchWartung(id!),
    enabled: id !== null,
  })
  const { data: arten } = useQuery({ queryKey: ["wartungsarten"], queryFn: fetchWartungsarten })
  const { data: anlagen } = useQuery({ queryKey: ["anlagen"], queryFn: fetchAnlagen })

  useEffect(() => {
    if (istNeu) setForm({ ...LEER, ...vorbelegung })
    else if (wartung) setForm(wartung)
  }, [istNeu, wartung, wartungId, vorbelegung])

  const speichern = useMutation({
    mutationFn: () => (istNeu ? createWartung(form) : updateWartung(id!, form)),
    onSuccess: () => {
      toast.success("Wartung gespeichert.")
      onGespeichert()
      onOpenChange(false)
    },
    onError: (err) => toast.error(fehlerNachricht(err, "Wartung konnte nicht gespeichert werden.")),
  })

  const loeschen = useMutation({
    mutationFn: () => deleteWartung(id!),
    onSuccess: () => {
      toast.success("Wartung gelöscht.")
      onGespeichert()
      onOpenChange(false)
    },
    onError: (err) => toast.error(fehlerNachricht(err, "Wartung konnte nicht gelöscht werden.")),
  })

  const duplizieren = useMutation({
    mutationFn: () => dupliziereWartung(id!),
    onSuccess: () => {
      toast.success("Wartung dupliziert.")
      onGespeichert()
      onOpenChange(false)
    },
    onError: (err) => toast.error(fehlerNachricht(err, "Wartung konnte nicht dupliziert werden.")),
  })

  const serviceticket = useMutation({
    mutationFn: () => erstelleWartungServiceticket(id!),
    onSuccess: () => toast.success("Serviceticket erstellt."),
    onError: (err) => toast.error(fehlerNachricht(err, "Serviceticket konnte nicht erstellt werden.")),
  })

  return (
    <Sheet open={wartungId !== null} onOpenChange={onOpenChange}>
      <SheetContent sheetKey="wartung-detail" defaultWidth={SHEET_WIDTH.md}>
        <SheetHeader>
          <SheetTitle>{istNeu ? "Neue Wartung/Inspektion" : `Wartung #${id}`}</SheetTitle>
          <SheetDescription>
            02-W — dieselbe Fristen-Engine wie Prüfungen, konsistent schaltjahrkorrekt (§5).
          </SheetDescription>
          {wartung && (
            <div className="flex items-center gap-2 text-sm">
              <span className="text-slate-600">Status:</span>
              <AmpelBadge ampel={wartung.ampel} />
              <span className="text-slate-500">Ablaufdatum: {wartung.ablaufdatum ?? "—"}</span>
            </div>
          )}
        </SheetHeader>

        <form
          className="grid gap-4"
          onSubmit={(e) => {
            e.preventDefault()
            speichern.mutate()
          }}
        >
          <div className="space-y-1.5">
            <label className="block text-sm text-slate-700">Wartungs-/Inspektionsart</label>
            <Select
              required
              value={form.wartungsartId || ""}
              onChange={(e) => setForm({ ...form, wartungsartId: Number(e.target.value) })}
            >
              <option value="">— auswählen —</option>
              {arten?.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.titel}
                </option>
              ))}
            </Select>
          </div>
          <div className="space-y-1.5">
            <label className="block text-sm text-slate-700">Anlage</label>
            <Select
              value={form.anlageId ?? ""}
              onChange={(e) =>
                setForm({ ...form, anlageId: e.target.value ? Number(e.target.value) : null })
              }
            >
              <option value="">—</option>
              {anlagen?.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.bezeichnung}
                </option>
              ))}
            </Select>
          </div>
          <div className="space-y-1.5">
            <label className="block text-sm text-slate-700">Wartungsdatum</label>
            <Input
              type="date"
              value={form.wartungsdatum ?? ""}
              onChange={(e) => setForm({ ...form, wartungsdatum: e.target.value || null })}
            />
          </div>
          <div className="space-y-1.5">
            <label className="block text-sm text-slate-700">Status</label>
            <Input
              value={form.status ?? ""}
              onChange={(e) => setForm({ ...form, status: e.target.value || null })}
            />
          </div>
          <div className="space-y-1.5">
            <label className="block text-sm text-slate-700">Ergebnis</label>
            <Textarea
              value={form.ergebnis ?? ""}
              onChange={(e) => setForm({ ...form, ergebnis: e.target.value })}
              rows={3}
            />
          </div>

          {!istNeu && (
            <div className="flex flex-wrap gap-2">
              <Button type="button" variant="secondary" onClick={() => duplizieren.mutate()}>
                Wartung duplizieren
              </Button>
              <Button type="button" variant="secondary" onClick={() => serviceticket.mutate()}>
                Serviceticket erstellen
              </Button>
            </div>
          )}
          {serviceticket.isSuccess && (
            <p className="text-sm text-green-700">Serviceticket erstellt: {serviceticket.data.ticketKey}</p>
          )}

          <SheetFooter>
            {!istNeu && (
              <Button type="button" variant="destructive" onClick={() => loeschen.mutate()}>
                Löschen
              </Button>
            )}
            <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
              Abbrechen
            </Button>
            <Button type="submit" variant="primary" disabled={speichern.isPending}>
              Speichern
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  )
}
